import { ReactNode, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Button,
  ButtonGroup,
  Divider,
  Paper,
  Avatar,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Battery20,
  Bolt,
  DeviceThermostat,
  TrendingUp,
  Info,
  DirectionsCar,
  Warning,
  SvgIconComponent,
  ArrowBack,
  BatteryChargingFull,
  Speed,
  Assessment
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '../../../containers/Sidebar/Sidebar';
import { CustomTypography } from '../../../components/CustomTypography/CustomTypography';
import { CustomLoader } from '../../../components/CustomLoader/CustomLoader';
import { useGetVehicleBatteryStats } from '../../../api/vehicule';
import useStyles from './styles';

// Define types for your data structures
interface VehicleInfo {
  full_name?: string;
  number?: string;
  vin?: string;
}

interface ChargingBehaviorScore {
  score?: number;
  mark?: string;
  count?: number;
}

interface ChargingBehavior {
  fast_charges?: ChargingBehaviorScore;
  deep_discharges?: ChargingBehaviorScore;
  full_charges?: ChargingBehaviorScore;
}

interface BatteryManagement {
  time_above_80_percent?: number;
  time_below_20_percent?: number;
  optimal_range_usage?: number;
}

interface TemperatureImpact {
  score?: number;
  mark?: string;
}

interface Distance {
  total_km?: number;
}

interface ScoreDetails {
  charging_behavior?: ChargingBehavior;
  battery_management?: BatteryManagement;
  temperature_impact?: TemperatureImpact;
  distance?: Distance;
}

interface Summary {
  health_status?: HealthStatus;
  primary_concern?: string;
  strongest_area?: string;
  improvement_potential?: string;
}

interface Period {
  from_date?: string;
  to_date?: string;
}

interface BatteryStats {
  final_score?: number;
  final_grade?: string;
  stateOfHealth?: number;
  score_details?: ScoreDetails;
  summary?: Summary;
  period?: Period;
  available?: boolean;
  message?: string;
}

interface BatteryData {
  retrieved_at?: string;
}

type HealthStatus = 'excellent' | 'good' | 'fair' | 'poor' | 'critical';

interface ScoreCardProps {
  title: string;
  score: number;
  mark: string;
  icon: SvgIconComponent;
  description?: string;
  recommendations?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}

interface MainScoreCardProps {
  title: string;
  children?: ReactNode;
  noData?: boolean;
  icon?: SvgIconComponent;
}

// Helper function to calculate date range based on period
const getDateRange = (period: string) => {
  const now = new Date();
  const toDate = now.toISOString();
  let fromDate: string;

  switch (period) {
    case '30 Days':
      fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      break;
    case '3 Month':
      fromDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      break;
    case '6 Month':
      fromDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000).toISOString();
      break;
    case '1 year':
      fromDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
      break;
    default:
      fromDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
  }

  return { fromDate, toDate };
};

export const BatteryHealthVehicule = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const vehicleId = parseInt(id);
  const [selectedPeriod, setSelectedPeriod] = useState('1 year');

  // Calculate date range based on selected period
  const dateRange = useMemo(() => getDateRange(selectedPeriod), [selectedPeriod]);

  const { batteryData, vehicleInfo, batteryStats, isLoading } = useGetVehicleBatteryStats(
    vehicleId, 
    dateRange.fromDate, 
    dateRange.toDate
  ) as {
    batteryData?: BatteryData;
    vehicleInfo?: VehicleInfo;
    batteryStats?: BatteryStats;
    isLoading: boolean;
  };

  const getScoreColor = (score: number): 'success' | 'primary' | 'warning' | 'error' => {
    if (score >= 90) return 'success';
    if (score >= 75) return 'primary';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getHealthStatusColor = (status?: HealthStatus): 'success' | 'primary' | 'warning' | 'error' => {
    if (!status) return 'primary';
    
    const colors: Record<HealthStatus, 'success' | 'primary' | 'warning' | 'error'> = {
      excellent: 'success',
      good: 'primary',
      fair: 'warning',
      poor: 'error',
      critical: 'error',
    };
    return colors[status];
  };

  const ScoreCard: React.FC<ScoreCardProps> = ({
    title,
    score,
    mark,
    icon: Icon,
    description,
    recommendations,
    color = 'primary',
  }) => (
    <Card className={classes.scoreCard}>
      <CardContent className={classes.scoreCardContent}>
        <Box className={classes.scoreCardHeader}>
          <Box className={classes.scoreCardTitleContainer}>
            <Icon color={color} className={classes.scoreCardIcon} />
            <Typography variant="h6" className={classes.scoreCardTitle}>
              {title}
            </Typography>
          </Box>
          <Chip 
            label={score} 
            color={getScoreColor(score)}
            size="small"
            variant="outlined"
            className={classes.scoreChip}
          />
        </Box>

        <Box className={classes.progressSection}>
          <Typography variant="body2" className={classes.markText}>
            {mark}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={score} 
            color={getScoreColor(score)}
            className={classes.progressBar}
          />
        </Box>

        {description && (
          <Typography variant="body2" className={classes.descriptionText}>
            {description}
          </Typography>
        )}

        {recommendations && (
          <Paper className={classes.recommendationBox}>
            <Typography variant="caption" className={classes.recommendationText}>
              <strong>Recommendation:</strong> {recommendations}
            </Typography>
          </Paper>
        )}
      </CardContent>
    </Card>
  );

  const MainScoreCard = ({ title, children, noData = false, icon: Icon }: MainScoreCardProps): JSX.Element => (
    <Card className={classes.mainCard}>
      <CardContent className={classes.mainCardContent}>
        <Box className={classes.mainCardHeader}>
          {Icon && <Icon className={classes.mainCardIcon} />}
          <Typography variant="h6" className={classes.mainCardTitle}>
            {title}
          </Typography>
          <Tooltip title="Battery care information">
            <IconButton
              aria-label={t("a11y.moreInfo", "More information")} size="small" className={classes.infoButton}>
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {noData ? (
          <Box className={classes.noDataContainer}>
            <Warning className={classes.noDataIcon} />
            <Typography className={classes.noDataText}>No data to show</Typography>
          </Box>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return <CustomLoader isActive={isLoading} />;
  }

  // Check if data is not available - but still show the UI with "No data to show"
  const hasData = batteryStats?.available;

  // Mock evolution data - you can replace this with real historical data
  const evolutionData = [
    { month: 'Oct', score: (batteryStats?.stateOfHealth ?? 0) - 5 },
    { month: 'Nov', score: batteryStats?.stateOfHealth ?? 0 },
  ];

  return (
    <>
      <Sidebar
        styles={{
          sidebarRightRoot: classes.batteryHealthRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        {/* Header */}
        <Box className={classes.containerHeader}>
          <Box className={classes.headerLeft}>
            <IconButton
              aria-label={t("a11y.goBack", "Go back")} 
              onClick={() => navigate(-1)} 
              className={classes.backButton}
            >
              <ArrowBack />
            </IconButton>
            <CustomTypography
              variant="h2"
              text={t("battery.healthTitle", "Battery Health")}
              isUppercase
            />
          </Box>
          <ButtonGroup variant="outlined" size="small" className={classes.periodSelector}>
            {['30 Days', '3 Month', '6 Month', '1 year'].map((period) => (
              <Button
                key={period}
                variant={selectedPeriod === period ? 'contained' : 'outlined'}
                onClick={() => setSelectedPeriod(period)}
                className={selectedPeriod === period ? classes.activePeriod : classes.inactivePeriod}
              >
                {period}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        {/* Vehicle Info Card */}
        <Paper className={classes.vehicleInfoCard}>
          <Box className={classes.vehicleInfoContent}>
            <Avatar className={classes.vehicleAvatar}>
              <DirectionsCar />
            </Avatar>
            <Box className={classes.vehicleDetails}>
              <Typography variant="h4" className={classes.vehicleName}>
                {vehicleInfo?.full_name}
              </Typography>
              <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                <Typography variant="body2" className={classes.vehicleMetadata}>
                  {vehicleInfo?.number}
                </Typography>
                <Typography variant="body2" className={classes.vehicleMetadata}>
                  {vehicleInfo?.vin}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Box className={classes.stateOfCharge}>
            <Typography variant="body2" className={classes.stateOfChargeLabel}>
              State of charge:
            </Typography>
            <Typography variant="body2" className={classes.stateOfChargeValue}>
              N/A
            </Typography>
          </Box>
        </Paper>

        {/* Main Stats */}
        <Grid container spacing={3} className={classes.mainStatsGrid}>
          <Grid item xs={12} md={4}>
            <MainScoreCard title="Battery Care Score" noData={!hasData} icon={BatteryChargingFull}>
              {hasData && (
                <Box className={classes.mainScoreContent}>
                  <Typography variant="h2" className={classes.mainScore}>
                    {batteryStats?.final_score}
                  </Typography>
                  <Typography variant="h6" className={classes.gradeText}>
                    Grade: {batteryStats?.final_grade}
                  </Typography>
                  <Chip
                    label={batteryStats?.summary?.health_status}
                    color={getHealthStatusColor(batteryStats?.summary?.health_status)}
                    variant="filled"
                    className={classes.healthStatusChip}
                  />
                </Box>
              )}
            </MainScoreCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <MainScoreCard title="SoH Projection" noData={!hasData} icon={Speed}>
              {hasData && (
                <Box className={classes.mainScoreContent}>
                  <Typography variant="h2" className={classes.mainScore}>
                    {batteryStats?.stateOfHealth?.toFixed(1)}%
                  </Typography>
                  <Typography variant="h6" className={classes.gradeText}>
                    State of Health
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={batteryStats?.stateOfHealth || 0} 
                      color={getScoreColor(batteryStats?.stateOfHealth || 0)}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" className={classes.footerText} sx={{ mt: 1, display: 'block' }}>
                      Current battery capacity retention
                    </Typography>
                  </Box>
                </Box>
              )}
            </MainScoreCard>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <MainScoreCard title="Battery Care Score Evolution" noData={!hasData} icon={Assessment}>
              {hasData && (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={evolutionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis hide />
                    <RechartsTooltip />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#1976d2" 
                      strokeWidth={3}
                      dot={{ fill: '#1976d2', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </MainScoreCard>
          </Grid>
        </Grid>

        {/* Detailed Scores */}
        <Grid container spacing={3} className={classes.detailedScoresGrid}>
          <Grid item xs={12} sm={6} lg={3}>
            {hasData ? (
              <ScoreCard
                title="Fast Charging score"
                score={batteryStats?.score_details?.charging_behavior?.fast_charges?.score || 0}
                mark={batteryStats?.score_details?.charging_behavior?.fast_charges?.mark || 'N/A'}
                icon={Bolt}
                color="warning"
                description="Frequent fast charging can degrade battery health over time. Use slow charging for daily needs, save fast charging for longer trips."
                recommendations="Limit fast charging to essential trips only"
              />
            ) : (
              <Card className={classes.noDataScoreCard}>
                <CardContent className={classes.noDataScoreCardContent}>
                  <Box className={classes.noDataScoreCardHeader}>
                    <Box className={classes.scoreCardTitleContainer}>
                      <Bolt color="warning" className={classes.scoreCardIcon} />
                      <Typography variant="h6" className={classes.scoreCardTitle}>
                        Fast Charging score
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={classes.noDataContainer}>
                    <Warning className={classes.noDataIcon} />
                    <Typography className={classes.noDataText}>No data to show</Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            {hasData ? (
              <ScoreCard
                title="Deep Discharge score"
                score={batteryStats?.score_details?.charging_behavior?.deep_discharges?.score || 0}
                mark={batteryStats?.score_details?.charging_behavior?.deep_discharges?.mark || 'N/A'}
                icon={Battery20}
                color="error"
                description="Deep discharges can stress the battery and reduce its lifespan. Try to avoid this as much as possible."
                recommendations="Avoid letting battery drop below 20%"
              />
            ) : (
              <Card className={classes.noDataScoreCard}>
                <CardContent className={classes.noDataScoreCardContent}>
                  <Box className={classes.noDataScoreCardHeader}>
                    <Box className={classes.scoreCardTitleContainer}>
                      <Battery20 color="error" className={classes.scoreCardIcon} />
                      <Typography variant="h6" className={classes.scoreCardTitle}>
                        Deep Discharge score
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={classes.noDataContainer}>
                    <Warning className={classes.noDataIcon} />
                    <Typography className={classes.noDataText}>No data to show</Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            {hasData ? (
              <ScoreCard
                title="Full Charge score"
                score={batteryStats?.score_details?.charging_behavior?.full_charges?.score || 0}
                mark={batteryStats?.score_details?.charging_behavior?.full_charges?.mark || 'N/A'}
                icon={TrendingUp}
                color="success"
                description="Frequent full charges can degrade battery health over time. Set daily charge limit to 80-90%, only charge to 100% before long trips."
                recommendations="Limit daily charging to 80-90%"
              />
            ) : (
              <Card className={classes.noDataScoreCard}>
                <CardContent className={classes.noDataScoreCardContent}>
                  <Box className={classes.noDataScoreCardHeader}>
                    <Box className={classes.scoreCardTitleContainer}>
                      <TrendingUp color="success" className={classes.scoreCardIcon} />
                      <Typography variant="h6" className={classes.scoreCardTitle}>
                        Full Charge score
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={classes.noDataContainer}>
                    <Warning className={classes.noDataIcon} />
                    <Typography className={classes.noDataText}>No data to show</Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            {hasData ? (
              <ScoreCard
                title="Temperature Impact score"
                score={batteryStats?.score_details?.temperature_impact?.score || 0}
                mark={batteryStats?.score_details?.temperature_impact?.mark || 'N/A'}
                icon={DeviceThermostat}
                color="primary"
                description="Charging or using your vehicle in extreme temperatures can degrade battery health."
                recommendations="Use preconditioning in extreme weather"
              />
            ) : (
              <Card className={classes.noDataScoreCard}>
                <CardContent className={classes.noDataScoreCardContent}>
                  <Box className={classes.noDataScoreCardHeader}>
                    <Box className={classes.scoreCardTitleContainer}>
                      <DeviceThermostat color="primary" className={classes.scoreCardIcon} />
                      <Typography variant="h6" className={classes.scoreCardTitle}>
                        Temperature Impact score
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={classes.noDataContainer}>
                    <Warning className={classes.noDataIcon} />
                    <Typography className={classes.noDataText}>No data to show</Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>

        {/* Additional Stats */}
        <Grid container spacing={3} className={classes.additionalStatsGrid}>
          <Grid item xs={12} md={4}>
            <Card className={classes.statsCard}>
              <CardContent className={classes.statsCardContent}>
                <Typography variant="h6" className={classes.statsCardTitle}>
                  Battery Management
                </Typography>
                <Stack spacing={2} className={classes.statsStack}>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Time above 80%</Typography>
                    <Typography variant="body2" className={classes.statValue}>
                      {Math.round((batteryStats?.score_details?.battery_management?.time_above_80_percent || 0) * 100)}%
                    </Typography>
                  </Box>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Time below 20%</Typography>
                    <Typography variant="body2" className={classes.statValue}>
                      {Math.round((batteryStats?.score_details?.battery_management?.time_below_20_percent || 0) * 100)}%
                    </Typography>
                  </Box>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Optimal range usage</Typography>
                    <Typography variant="body2" className={classes.statValueSuccess}>
                      {Math.round((batteryStats?.score_details?.battery_management?.optimal_range_usage || 0) * 100)}%
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.statsCard}>
              <CardContent className={classes.statsCardContent}>
                <Typography variant="h6" className={classes.statsCardTitle}>
                  Charging Statistics
                </Typography>
                <Stack spacing={2} className={classes.statsStack}>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Total distance</Typography>
                    <Typography variant="body2" className={classes.statValue}>
                      {(batteryStats?.score_details?.distance?.total_km || 0).toLocaleString()} km
                    </Typography>
                  </Box>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Fast charges</Typography>
                    <Typography variant="body2" className={classes.statValue}>
                      {batteryStats?.score_details?.charging_behavior?.fast_charges?.count || 0}
                    </Typography>
                  </Box>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Full charges</Typography>
                    <Typography variant="body2" className={classes.statValue}>
                      {batteryStats?.score_details?.charging_behavior?.full_charges?.count || 0}
                    </Typography>
                  </Box>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Deep discharges</Typography>
                    <Typography variant="body2" className={classes.statValueWarning}>
                      {batteryStats?.score_details?.charging_behavior?.deep_discharges?.count || 0}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card className={classes.statsCard}>
              <CardContent className={classes.statsCardContent}>
                <Typography variant="h6" className={classes.statsCardTitle}>
                  Health Summary
                </Typography>
                <Stack spacing={2} className={classes.statsStack}>
                  <Box className={classes.statRowWithChip}>
                    <Typography variant="body2" className={classes.statLabel}>Health status</Typography>
                    <Chip 
                      label={batteryStats?.summary?.health_status}
                      color={getHealthStatusColor(batteryStats?.summary?.health_status)}
                      size="small"
                      className={classes.healthStatusSmallChip}
                    />
                  </Box>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Primary concern</Typography>
                    <Typography variant="body2" className={classes.statValueCapitalized}>
                      {batteryStats?.summary?.primary_concern?.replace('_', ' ') || 'None'}
                    </Typography>
                  </Box>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Strongest area</Typography>
                    <Typography variant="body2" className={classes.statValueSuccessCapitalized}>
                      {batteryStats?.summary?.strongest_area || 'N/A'}
                    </Typography>
                  </Box>
                  <Box className={classes.statRow}>
                    <Typography variant="body2" className={classes.statLabel}>Improvement potential</Typography>
                    <Typography variant="body2" className={classes.statValueCapitalized}>
                      {batteryStats?.summary?.improvement_potential?.replace('_', ' ') || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box className={classes.footer}>
          <Typography variant="caption" className={classes.footerText}>
            Data retrieved at: {batteryData?.retrieved_at ? new Date(batteryData.retrieved_at).toLocaleString() : 'N/A'}
          </Typography>
          <Typography variant="caption" className={classes.footerText}>
            Analysis period: {batteryStats?.period?.from_date || 'N/A'} to {batteryStats?.period?.to_date || 'N/A'}
          </Typography>
        </Box>
      </Sidebar>
    </>
  );
};